/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, OnDestroy, OnInit, signal} from '@angular/core';
import {StateService, UIRouter} from '@uirouter/core';
import * as _ from 'lodash';

import template from './collapsible-sidebar.html';
import {OBJECT_TYPE} from '../constants/object-type';
import {LocalNetworkInfo} from '../../../components/session/local-network-info';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../../components/session/local-user-permissions';
import {AppShellStateService} from '../../base/app-shell-state.service';
import {LocalUserProfile} from '../../../components/session/local-user-profile';
import {canAccessNetworkExplore} from '../../explore/network-explore-access';
import {BrowserLocationService} from '../../angular-platform-providers';
import {deferSignalStateWrite} from '../util/template-state';

const USER_MENUS = ['app.adm.users', 'app.adm.user-profile', 'app.adm.team-groups', 'app.adm.teams', 'app.adm.roles', 'app.adm.notification-preferences', 'app.adm.api-keys'];
const DESIGN_MENUS = ['app.adm.layouts', 'app.adm.layout-includes', 'app.adm.layout-examples', 'app.adm.marketplace-products', 'app.adm.products', 'app.adm.coupons', 'app.adm.email-translations', 'app.adm.email-templates'];
const QUERY_MENUS = ['app.rep.queries', 'app.data-queries'];
const DATA_EXPORT_ADMIN_MENUS = ['app.adm.data-exports'];

const DEFAULT_MENU_VIEW: Record<string, boolean> = {
  dashboards: false,
  advertising: false,
  political: false,
  inventory: false,
  reports: false,
  queries: false,
  users: false,
  design: false,
  admin: false,
  segmentation: false,
  fields: false
};

@Component({
  standalone: false,
  selector: 'adn-collapsible-sidebar',
  template
})
export class CollapsibleSidebarComponent implements OnInit, OnDestroy {
  readonly OBJECT_TYPE = OBJECT_TYPE;

  private readonly permsState = signal<any>({});
  private readonly anyPermsState = signal<any>({});
  private readonly sectionPermsState = signal<any>({});
  private readonly menuViewState = signal<Record<string, boolean>>({...DEFAULT_MENU_VIEW});
  private readonly menuViewFromPageState = signal<Record<string, boolean>>({...DEFAULT_MENU_VIEW});
  private readonly appState = signal('');
  private readonly showEmailTranslationsExtraState = signal(false);

  get perms(): any { return this.permsState(); }
  private set perms(value: any) { this.permsState.set(value); }
  get anyPerms(): any { return this.anyPermsState(); }
  private set anyPerms(value: any) { this.anyPermsState.set(value); }
  get sectionPerms(): any { return this.sectionPermsState(); }
  private set sectionPerms(value: any) { this.sectionPermsState.set(value); }
  get menuView(): Record<string, boolean> { return this.menuViewState(); }
  private set menuView(value: Record<string, boolean>) { this.menuViewState.set(value); }
  get menuViewFromPage(): Record<string, boolean> { return this.menuViewFromPageState(); }
  private set menuViewFromPage(value: Record<string, boolean>) { this.menuViewFromPageState.set(value); }
  get app(): string { return this.appState(); }
  private set app(value: string) { this.appState.set(value); }
  get showEmailTranslationsExtra(): boolean { return this.showEmailTranslationsExtraState(); }
  private set showEmailTranslationsExtra(value: boolean) { this.showEmailTranslationsExtraState.set(value); }

  experimental = false;
  suiPlatform = false;
  marketplacePlatform = false;
  pureMarketplacePlatform = false;
  showProducts = false;
  showFolios = false;
  showMarketplaceReviews = false;
  showNetworkExplore = false;
  showPolitical = false;
  showReleaseNotesAdmin = false;

  private deregisterTransitions: () => void;

  constructor(
    readonly shell: AppShellStateService,
    private readonly localUserPermissions: LocalUserPermissions,
    private readonly localNetworkProfile: LocalNetworkProfile,
    private readonly uiRouter: UIRouter,
    @Inject(BrowserLocationService) private readonly location: any,
    private readonly $state: StateService,
    @Inject(LocalUserProfile) private readonly localUserProfile: any,
    private readonly localNetworkInfo: LocalNetworkInfo = new LocalNetworkInfo()
  ) {
  }

  ngOnInit(): void {
    this.experimental = this.localNetworkProfile.isExperimental()
      || this.localUserProfile.get('username') === 'office-mel@adnuntius.com'
      || this.location.hostname === 'localhost';
    this.suiPlatform = this.localNetworkProfile.isSuiPlatform();
    this.marketplacePlatform = this.localNetworkProfile.isMarketplacePlatform();
    this.pureMarketplacePlatform = this.localNetworkProfile.isPureMarketplacePlatform();
    this.showProducts = this.localNetworkInfo.hasProductFeedProducts();
    this.showFolios = this.localNetworkInfo.getInfo('creativeSetCount') + this.localNetworkInfo.getInfo('folioCount') > 0;
    this.showMarketplaceReviews = this.localNetworkInfo.getInfo('axProductsWithReviewCount');
    this.showNetworkExplore = canAccessNetworkExplore(this.localUserProfile, this.localUserPermissions);
    this.showPolitical = !!this.localNetworkProfile.get('politicalApp');
    this.showReleaseNotesAdmin = ['adnuntius@adnuntius.com', 'broker1@adnuntius.com'].indexOf(this.localUserProfile.get('username')) > -1;

    this.selectMenu();
    this.deregisterTransitions = this.uiRouter.transitionService.onSuccess({}, () => {
      deferSignalStateWrite(() => this.selectMenu());
    }) as () => void;
  }

  ngOnDestroy(): void {
    if (this.deregisterTransitions) {
      this.deregisterTransitions();
    }
  }

  sectionClicked(menuViewParam: string): void {
    const menuView = {...this.menuView, [menuViewParam]: !this.menuView[menuViewParam]};
    if (!menuView[menuViewParam]) {
      this.menuView = menuView;
      return;
    }
    _.forEach(menuView, (_value, key) => {
      if (menuViewParam !== key && !this.menuViewFromPage[key]) {
        menuView[key] = false;
      }
    });
    this.menuView = menuView;
  }

  trackWorkspace(_index: number, workspace: any): any {
    return workspace.id;
  }

  private selectMenu(): void {
    const app = this.localUserProfile.getCurrentApp();
    const perms = this.localUserPermissions.getAllNetworkPermissions();
    const anyPerms = this.localUserPermissions.getAnyVisibility();
    const sectionPerms = this.localUserPermissions.getSectionPerms(app);
    const menuView = {...DEFAULT_MENU_VIEW};

    const urlState = _.get(this.$state.current, 'name') || '';
    if (app === 'DATA') {
      if (this.$state.includes('app.dat-seg')) {
        menuView.segmentation = true;
      } else if (this.$state.includes('app.dat-map.fields') || this.$state.includes('app.dat-map.mappings')) {
        menuView.fields = true;
      } else if (this.$state.includes('app.data-queries')) {
        menuView.queries = true;
      }
    }
    if (app === 'POLITICAL'
      && (this.$state.includes('app.pol.political-entities')
        || this.$state.includes('app.pol.political-campaigns')
        || this.$state.includes('app.welcome-political'))) {
      menuView.political = true;
    }
    if (app === 'ADVERTISING') {
      if (this.$state.includes('app.adv')
        || urlState.indexOf('app.reach-analysis') > -1
        || urlState.indexOf('app.explore') > -1
        || urlState.indexOf('app.booking-calendar') > -1
        || urlState.indexOf('app.adv.third-party-audiences') > -1) {
        menuView.advertising = true;
      } else if (this.$state.includes('app.inv') || urlState.indexOf('app.ad-tag') > -1) {
        menuView.inventory = true;
      } else if (this.$state.includes('app.wor') || urlState.indexOf('app.wor') > -1) {
        menuView.dashboards = true;
      }
    }
    if (this.$state.includes('app.rep')) {
      menuView[QUERY_MENUS.some((menu) => urlState.indexOf(menu) > -1) ? 'queries' : 'reports'] = true;
    } else if (this.$state.includes('app.adm')
      || this.$state.includes('app.simple.release-notes')
      || this.$state.includes('app.simple.release-notes.release-note')) {
      if (USER_MENUS.some((menu) => urlState.indexOf(menu) > -1)) {
        menuView.users = true;
      } else if (DESIGN_MENUS.some((menu) => urlState.indexOf(menu) > -1)) {
        menuView.design = true;
      } else if (DATA_EXPORT_ADMIN_MENUS.some((menu) => urlState.indexOf(menu) > -1)
        && (perms.AX_ADVERTISER_ADMIN || perms.AX_PUBLISHER_ADMIN)) {
        menuView.queries = true;
      } else {
        menuView.admin = true;
      }
    }

    this.app = app;
    this.perms = perms;
    this.anyPerms = anyPerms;
    this.sectionPerms = sectionPerms;
    this.menuView = menuView;
    this.menuViewFromPage = {...menuView};
    this.showEmailTranslationsExtra = this.localNetworkInfo.getInfo('emailTranslationsCount') > 0
      && (anyPerms.AX_PUBLISHER_TEAM_ADMIN
        || anyPerms.AX_PUBLISHER_ADMIN
        || anyPerms.AX_ADVERTISER_ADMIN)
      && !anyPerms.MANAGE_SYSTEM;
  }
}
